import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Switch,
  Typography,
  Box,
  CircularProgress,
  FormControlLabel,
} from "@mui/material";
import { useEffect, useState } from "react";
import NotesIcon from "@mui/icons-material/NotesOutlined";
import InitialsAvatar from "src/components/InitialsAvatar";

const STATUS = ["NEW", "CONTACTED", "RESCHEDULED", "QUALIFIED", "WON", "LOST"];

const UpdateContactModal = ({
  open,
  onClose,
  data,
  onSave,
  loading = false,
}: {
  open: boolean;
  onClose: () => void;
  data: any;
  onSave: (payload: any) => void;
  loading?: boolean;
}) => {
  const [status, setStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [contacted, setContacted] = useState(false);

  useEffect(() => {
    if (data) {
      setStatus(data.status);
      setRemarks(data.remarks || "");
      setContacted(data.contacted);
    }
  }, [data]);

  const handleStatusChange = (val: string) => {
    setStatus(val);
    if (val === "CONTACTED" || val === "WON") setContacted(true);
  };

  const handleSave = () => onSave({ status, remarks, contacted });

  if (!data) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1.75, display: "flex", alignItems: "center", gap: 1.25, borderBottom: "1px solid #EEF2F6" }}>
        <InitialsAvatar name={data.name} size={38} />
        <Box minWidth={0}>
          <Typography fontSize="16px" fontWeight={800} letterSpacing="-0.2px" noWrap>{data.name}</Typography>
          <Typography fontSize="13px" color="text.secondary" noWrap>{data.company || data.email}</Typography>
        </Box>
      </Box>

      <DialogContent>
        <Stack spacing={2.25} mt={0.5}>
          <Box>
            <Typography fontSize="12px" fontWeight={700} color="text.secondary" mb={0.75} textTransform="uppercase" letterSpacing="0.04em">Status</Typography>
            <TextField select fullWidth size="small" value={status} onChange={(e) => handleStatusChange(e.target.value)}>
              {STATUS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <FormControlLabel
              control={<Switch checked={contacted} onChange={(e) => setContacted(e.target.checked)} />}
              label={<Typography fontSize="13.5px" fontWeight={600}>Marked as Contacted</Typography>}
            />
          </Box>

          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
              <NotesIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography fontSize="12px" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing="0.04em">Internal Remarks</Typography>
            </Stack>
            <TextField multiline minRows={4} fullWidth size="small" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Add conversation summary, follow-up notes, objections, etc." />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: "none", fontWeight: 600 }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading} sx={{ minWidth: 120, textTransform: "none", fontWeight: 600, borderRadius: "10px" }}>
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} color="inherit" />
              <span>Saving...</span>
            </Box>
          ) : (
            "Save Changes"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateContactModal;
